import {
  MutableRefObject, DragEvent, useState, useRef,
} from 'react';
import { PDFDocument } from 'pdf-lib';
import styled from 'styled-components';
import { Pdf, Button, MobileFlex } from '..';

const Center = styled.div<{show: boolean}>`
  text-align:center;
  ${(props) => (props.show ? 'height:100%;' : '')}
`;

const Container = styled.div`
  width:100%; 
  height:100%;
  display:inline-block;
`;

const DragDropContainer = styled.div`
  height:100%;
`;

const FileInput = styled.input`
  display: none;
`;

const FileDragAndDrop = styled.div`${({ theme }) => `
  border: 1px dashed ${theme.borderFaint};
  border-radius: ${theme.borderRadius[1]};
  height:100%;
  width:100%;
`}`;

const ButtonContainer = styled(MobileFlex)`
  justify-content:flex-end;
`;

export type FileUploadType = {
  documentType: string,
  documentId: string,
  filename: string,
  base64: string,
}

export function FileUpload(
  {
    splitFiles = true,
    show = true,
    close = () => {},
    showClose = true,
    fileTypeOptions = {},
    upload,
  }:
    {
      splitFiles?: boolean,
      show?: boolean,
      close?: ()=>void,
      showClose?: boolean,
      fileTypeOptions?: {[key: string]: string},
      upload: (fileUpload: FileUploadType[])=>{},
    },
) {
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [pageCounts, setPageCounts] = useState<{[key: string] : number}>({});
  const [pageTypes, setPageTypes] = useState<{[documentKey: string]:{[pageKey: string]: string}}>({});
  const fileInputRef = useRef() as MutableRefObject<HTMLInputElement>;

  const onClickFileDragAndDrop = () => {
    fileInputRef.current.click();
  };

  const createFile = async (id: string, filename: string, file: PDFDocument, documentType: string): Promise<FileUploadType> => {
    const base64 = `${await file.saveAsBase64()}`;

    return {
      documentType,
      documentId: id,
      filename,
      base64,
    };
  };

  const onFileUpload = async () => {
    if (selectedFiles !== null) {
      const files = await Promise.all(Array.from(selectedFiles).map(async (selectedFile, index) => {
        const file = await PDFDocument.load(await selectedFile.arrayBuffer());

        if (splitFiles) {
          return Promise.all([...Array(pageCounts[selectedFile.name])].map(async (_x, pageIndex) => {
            const newFile: PDFDocument = await PDFDocument.create();
            const [page] = await newFile.copyPages(file, [pageIndex]);
            newFile.addPage(page);

            return createFile(
              pageIndex.toString(),
              `${selectedFile.name}-${pageIndex.toString()}`,
              newFile,
              pageTypes[selectedFile.name][pageIndex],
            );
          }));
        }

        return createFile(index.toString(), selectedFile.name, file, pageTypes[selectedFile.name][0]);
      }));

      close();
      upload(files.flat());
    }
  };

  const onClickCancel = () => {
    setSelectedFiles(null);
    close();
  };

  const onDocumentLoadSuccess = (filename: string) => ({ numPages }: { numPages:number }) => {
    setPageCounts((prevPageCounts) => ({
      ...prevPageCounts,
      [filename]: splitFiles ? numPages : 1,
    }));

    setPageTypes((prevPageTypes) => ({
      ...prevPageTypes,
      [filename]: [...Array(numPages)].reduce((prev, _current, index) => ({
        ...prev,
        [index]: Object.keys(fileTypeOptions)[0],
      }), {}),
    }));
  };

  const handleDragAndDrop = (event: DragEvent<HTMLInputElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event: DragEvent<HTMLInputElement>) => {
    handleDragAndDrop(event);
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      setSelectedFiles(event.dataTransfer.files);
      event.dataTransfer.clearData();
    }
  };

  return (
    <Center show={show}>
      {show && (
      <Container>
        {selectedFiles === null && (
          <DragDropContainer>
            <FileInput
              id="file-input"
              ref={fileInputRef}
              type="file"
              onChange={(event: any) => setSelectedFiles(event?.target?.files)}
              multiple
            />
            <FileDragAndDrop
              onClick={onClickFileDragAndDrop}
              onDrop={handleDrop}
              onDragOver={handleDragAndDrop}
              onDragEnter={handleDragAndDrop}
              onDragLeave={handleDragAndDrop}
            >
              Click or Drag and Drop File
            </FileDragAndDrop>
          </DragDropContainer>
        )}

        {selectedFiles && Array.from(selectedFiles).map((selectedFile) => (
          <Pdf
            onDocumentLoadSuccess={onDocumentLoadSuccess(selectedFile.name)}
            selectedFile={selectedFile}
            selectedFileIndex={selectedFile.name}
            pageCount={pageCounts}
            pageTypes={pageTypes}
            setPageTypes={setPageTypes}
            fileTypeOptions={fileTypeOptions}
          />
        ))}

        <ButtonContainer>
          {selectedFiles && (
            <Button id="file-upload-button" onClick={onFileUpload}>Upload</Button>
          )}
          {(showClose || selectedFiles) && (
            <Button onClick={onClickCancel}>Cancel</Button>
          )}
        </ButtonContainer>
      </Container>
      )}
    </Center>
  );
}
